/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useEffect, useMemo } from "react";

//@Packages
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import DynamicForm from "../../form/DynamicForm";

//@Models
import { NiagaraServiceDetailsProps, IFormData, FormElementType, IFormElementConfig } from "../../../../models/softwaredetails";

//@Store
import { RootState } from "../../../../store";

//@Services
import { useGetTargetForgePointsQuery } from "../../../../services/software-details-api";
import { setTargetForgePoints } from "../../../../actions";

//@Utils
import Tzdb from "../../../../utils/tzdb";
import { SOFTWARE_CONSTANTS } from "../../../../utils/constants";
import "./index.scss";

const PASSWORD_PATTERN = /(?=^.{10,}$)(?=.*\d)(?=.*[A-Z])(?=.*[a-z]).*$/;
const NO_SPACES_PATTERN = /^\S*$/;

const DEFAULT_TARGET_FORGE_POINTS = [
  { value: "0 - 10,000", text: "0 - 10,000" },
  { value: "10,000 - 25,000", text: "10,000 - 25,000" },
];

const NiagaraServiceDetails: React.FC<NiagaraServiceDetailsProps> = ({
  title,
  subTitle,
  config = {},
  onSubmit,
  onCancel,
  cancelEnabled = true,
  softwareId,
  softwareKey,
  gatewayType,
  fieldProps = {},
  showWarningMessage = false,
  notificationProps
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Form field constants (matching original static readonly)
  const FIELD_PF_USER = "platformUser";
  const FIELD_PF_PASSWORD = "platformPassword";
  const FIELD_PF_PASSWORD_CONFIRM = "platformPasswordConfirm";
  const FIELD_PF_PASSPHRASE = "platformPassphrase";
  const FIELD_PF_PASSPHRASE_CONFIRM = "platformPassphraseConfirm";
  const PF_TIMEZONE = "niagaraTimezone";
  const TARGET_FORGE_POINTS = "targetForgePoints";

  // Blacklisted usernames (exact copy from original)
  const NIAGARA_BLACKLISTED_USERNAME = [
    'adm', 'admin', 'amanda', 'apache', '_apt', 'avahi', 'avahi-autoipd', 'backup',
    'bin', 'canna', 'cloud-print-connector', 'colord', 'cups', 'cups-pk-helper', 'daemon', 'dbus', 'dnsmasq', 'dovecot',
    'exim', 'freerad', 'ftp', 'games', 'gdm', 'geoclue', 'gitdaemon', 'gnats', 'gopher', 'halt', 'hplip', 'htt', 'http',
    'irc', 'kernoops', 'kubernetes', 'landscape', 'ldap', 'lightdm', 'list', 'lp', 'lxd', 'mail', 'mailman', 'mailnull',
    'majordom', 'man', 'messagebus', 'mysql', 'named', 'netdump', 'news', 'nobody', 'nscd', 'nsfnobody', 'ntp', 'operator',
    'pcap', 'pollinate', 'postfix', 'postgres', 'proxy', 'pulse', 'pvm', 'quagga', 'radiusd', 'radvd', 'root', 'rpc',
    'rpcuser', 'rpm', 'rtkit', 'saned', 'shutdown', 'smmsp', 'speech-dispatcher', 'squid', 'sshd', 'sslh', 'statd', 'sync',
    'sys', 'syslog', 'systemd-bus-proxy', 'systemd-coredump', 'systemd-journal-gateway', 'systemd-journal-remote',
    'systemd-network', 'systemd-resolve', 'systemd-timesync', 'usbmux', 'uucp', 'uuidd', 'vcsa', 'vnstat', 'webalizer',
    'whoopsie', 'wnn', 'www-data', 'xfs', 'xrdp', 'niagara'
  ];

  // Redux state
  const { softwareDetails } = useSelector((state: RootState) => state.global);

  const {
    data: targetForgePointsData,
    isLoading: targetForgePointsLoading
  } = useGetTargetForgePointsQuery(
    { softwareId, softwareKey },
    { skip: !softwareId || gatewayType !== "VIRTUAL" }
  );

  useEffect(() => {
    if (targetForgePointsData) {
      dispatch(setTargetForgePoints({ targetForgePoints: targetForgePointsData }));
    }
  }, [targetForgePointsData, dispatch]);


  const checkIfFieldDisabled = (key: string) => {
    try {
      return fieldProps?.[key]?.disabled || false;
    } catch (error) {
      return false;
    }
  };

  // Get timezone options (equivalent to getInputForNiagaraTimeZone logic)
  const timezoneOptions = useMemo(() => {
    return Tzdb.tzList.map((timeZone) => ({
      value: timeZone.id,
      text: timeZone.displayName 
        ? `${timeZone.id}(${timeZone.displayName})` 
        : timeZone.id
    }));
  }, []);

  const targetForgePointsOptions = useMemo(() => {
    if (softwareDetails?.targetForgePoints?.length > 0) {
      return softwareDetails.targetForgePoints.map((point: any) => ({
        value: point.name,
        text: point.name
      }));
    }
    return DEFAULT_TARGET_FORGE_POINTS;
  }, [softwareDetails?.targetForgePoints]);

    // Build form configuration
  const formConfig: IFormElementConfig[] = useMemo(() => {
    const fields: IFormElementConfig[] = [
      {
        type: FormElementType.TEXT,
        id: FIELD_PF_USER,
        label: "Platform Username",
        placeholder: "Enter Platform Username",
        value: config[FIELD_PF_USER],
        disabled: checkIfFieldDisabled(FIELD_PF_USER),
        validators: [
          {
            type: 'required',
            message: 'Cannot be blank'
          },
          {
            type: 'pattern',
            value: NO_SPACES_PATTERN,
            message: 'No Spaces Allowed'
          },
          {
            type: 'custom',
            message: 'Invalid Niagara Username',
            validate: (value: string) => {
              if (NIAGARA_BLACKLISTED_USERNAME.includes(value?.toLowerCase())) {
                return 'Invalid Niagara Username';
              }
              return '';
            }
          }
        ]
      },
      {
        type: FormElementType.PASSWORD,
        id: FIELD_PF_PASSWORD,
        label: "Platform Password",
        placeholder: "Enter Platform Password",
        value: config[FIELD_PF_PASSWORD],
        disabled: checkIfFieldDisabled(FIELD_PF_PASSWORD),
        validators: [
          {
            type: 'minLength',
            value: 10,
            message: 'Must contain a minimum of 10 characters'
          },
          {
            type: 'pattern',
            value: PASSWORD_PATTERN,
            message: 'Must contain at least one digit, upper and lower case letter'
          }
        ]
      },
      {
        type: FormElementType.PASSWORD,
        id: FIELD_PF_PASSWORD_CONFIRM,
        label: "Confirm Password",
        placeholder: "Confirm Password",
        value: config[FIELD_PF_PASSWORD_CONFIRM],
        disabled: checkIfFieldDisabled(FIELD_PF_PASSWORD_CONFIRM),
        validators: [
          {
            type: 'match',
            matchField: FIELD_PF_PASSWORD,
            message: 'Should match the platform password'
          }
        ]
      },
      {
        type: FormElementType.PASSWORD,
        id: FIELD_PF_PASSPHRASE,
        label: "Platform Passphrase",
        placeholder: "Enter Platform Passphrase",
        value: config[FIELD_PF_PASSPHRASE],
        disabled: checkIfFieldDisabled(FIELD_PF_PASSPHRASE),
        validators: [
          {
            type: 'minLength',
            value: 10,
            message: 'Minimum of 10 characters required'
          },
          {
            type: 'pattern',
            value: NO_SPACES_PATTERN,
            message: 'No Spaces Allowed'
          }
        ]
      },
      {
        type: FormElementType.PASSWORD,
        id: FIELD_PF_PASSPHRASE_CONFIRM,
        label: "Confirm Platform Passphrase",
        placeholder: "Confirm Passphrase",
        value: config[FIELD_PF_PASSPHRASE_CONFIRM],
        disabled: checkIfFieldDisabled(FIELD_PF_PASSPHRASE_CONFIRM),
        validators: [
          {
            type: 'match',
            matchField: FIELD_PF_PASSPHRASE,
            message: 'Should match the platform passphrase'
          }
        ]
      },
      {
        type: FormElementType.SELECT,
        id: PF_TIMEZONE,
        label: "Select Timezone",
        placeholder: "Select Timezone",
        value: config[PF_TIMEZONE],
        disabled: checkIfFieldDisabled(PF_TIMEZONE),
        options: timezoneOptions,
        validators: [
          {
            type: 'required',
            message: 'Cannot be blank'
          }
        ]
      }
    ];

    // Add Target Forge Points
    fields.push({
      type: FormElementType.SELECT,
      id: TARGET_FORGE_POINTS,
      label: "Target Forge Points",
      placeholder: targetForgePointsLoading 
        ? "Loading forge points..." 
        : "Select Target Forge Points",
      value: config[TARGET_FORGE_POINTS],
      disabled: targetForgePointsLoading || checkIfFieldDisabled(TARGET_FORGE_POINTS),
      options: targetForgePointsOptions,
      validators: [
        {
          type: 'required',
          message: 'Cannot be blank'
        }
      ]
    });

    return fields;
  }, [
    config, 
    gatewayType, 
    timezoneOptions, 
    targetForgePointsOptions, 
    targetForgePointsLoading,
    fieldProps
  ]);

    // Handle form submission - pass raw form data to parent for transformation
  const handleSubmit = (formData: IFormData) => {
    if (onSubmit) {
      onSubmit(formData);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <DynamicForm
      title={title}
      subTitle={subTitle}
      config={formConfig}
      onSubmit={handleSubmit}
      onCancel={cancelEnabled ? handleCancel : undefined}
      submitLabel={SOFTWARE_CONSTANTS.install}
      cancelLabel={SOFTWARE_CONSTANTS.cancel}
      showWarningMessage={showWarningMessage}
      notificationProps={notificationProps}
    />
  );
};

export default NiagaraServiceDetails;